// axios client for FastAPI backend
// - cookie session via withCredentials: true
// - unwrap envelope {data, error, ts, source}
// - 401 -> redirect /login
import axios from 'axios'

const client = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 30000,  // 真 30s 全局 (LLM diagnose 可超 15s)
  headers: { 'Content-Type': 'application/json' },
})

// 401 -> hard redirect (router not yet imported here to avoid cycle)
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const here = window.location.pathname
      if (here !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

// Unwrap envelope; throw on error field
// ADR-WEB-12.18 (Watson REVISE positions): 透出 stale + last_update 让页面能显示
// 数据来源/缓存状态 (持仓页用户感知"数据不对"部分来自不知道缓存延迟)
// ADR-WEB-12.34 r2 (codex 反审锁定): account inflight guard.
//   - 响应 envelope.account 或 body._account 标识响应实际归属哪个账户.
//   - 若与 window._maris_active_account (前端当前账户) 不匹配 → 抛 StaleAccountError,
//     调用方 catch 后丢弃 (防切账户瞬间旧 inflight 响应覆盖新视图).
//   - 兼容: 旧 endpoint 不返 account → 不校验 (向后兼容).
class StaleAccountError extends Error {
  constructor(expected, actual) {
    super(`stale account response: expected ${expected}, got ${actual}`)
    this.name = 'StaleAccountError'
    this.expected = expected
    this.actual = actual
    this.stale_account = true
  }
}

async function unwrap(promise) {
  const resp = await promise
  const body = resp?.data ?? {}
  if (body.error) {
    const err = new Error(typeof body.error === 'string' ? body.error : JSON.stringify(body.error))
    err.envelope = body
    throw err
  }
  // ADR-WEB-12.34 r2: account inflight guard
  const expectedAccount = (typeof window !== 'undefined' && window._maris_active_account) || null
  const responseAccount = body.account || (body.data && typeof body.data === 'object' && (body.data._account || body.data.account)) || null
  if (expectedAccount && responseAccount && expectedAccount !== responseAccount) {
    throw new StaleAccountError(expectedAccount, responseAccount)
  }
  return {
    data: body.data,
    ts: body.ts,
    source: body.source,
    stale: body.stale,
    last_update: body.last_update,
    account: responseAccount,
  }
}

export const api = {
  // auth
  login: (email, password) => unwrap(client.post('/auth/login', { email, password })),
  logout: () => unwrap(client.post('/auth/logout')),
  whoami: () => unwrap(client.get('/auth/whoami')),
  // ADR-WEB-12.31 件B PR5: 切账户 (testnet/live/copy)
  switchAccount: (account) => unwrap(client.post('/auth/switch-account', { account })),

  // health (no session required)
  health: () => unwrap(client.get('/health')),

  // changelog
  scripts: (repo, limit = 50) =>
    unwrap(client.get('/changelog/scripts', { params: { repo, limit } })),
  // FULLCHAIN-1 (2026-07-02, GAP-06/GAP-08): TRUTH 候选 state 快照 (扫描漏斗+入选池)
  scanSnapshot: () => unwrap(client.get('/changelog/scan-snapshot')),
  // FULLCHAIN-2 (2026-07-02, GAP-01/02/03/04): 守护/投递/学习
  guardianEvents: (sinceHours = 72) => unwrap(client.get('/changelog/guardian-events', { params: { since_hours: sinceHours } })),
  notifyDeliveries: (sinceHours = 24, limit = 300) => unwrap(client.get('/changelog/notify-deliveries', { params: { since_hours: sinceHours, limit } })),
  learningTrace: (symbol = null, days = 7) => unwrap(client.get('/changelog/learning-trace', { params: symbol ? { symbol, days } : { days } })),
  agentActions: (params = {}) =>
    unwrap(client.get('/changelog/agent-actions', { params })),
  // ADR-WEB-12.31: AI rationale 翻译 (deepseek-v4-flash, 批量 ≤50, sqlite cache)
  aiRationaleTranslate: (rationales) =>
    unwrap(client.post('/changelog/ai-rationale-translate', { rationales })),

  // ADR-WEB-12.51 (BL123): 读已持久化翻译缓存全表 (GET, 无 LLM), 前端 onMounted hydrate 历史翻译
  aiRationaleTranslationCache: () =>
    unwrap(client.get('/changelog/ai-rationale-translate-cache')),

  // BL124 (ADR-WEB-12.52): 仓位操作 tab raw JSON 精准翻译 (结构化 deepseek, lazy 展开触发).
  // items: [{key, kind, json_text}]. kind ∈ upstream_signal|trade_anchor|intent_chain|mmh_observation|unknown.
  // 返 {results:[{key,kind,cache_hit,zh:{summary,key_facts,risk_notes,omitted_paths},latency_ms,fallback_reason}]}.
  // LLM 调用 (脱敏+canonical+结构化), 给足超时.
  rawJsonTranslate: (items) =>
    unwrap(client.post('/changelog/raw-json-translate', { items }, { timeout: 60000 })),

  // BL124: 读已持久化 raw JSON 翻译缓存全表 (GET, 无 LLM), onMounted hydrate.
  rawJsonTranslationCache: () =>
    unwrap(client.get('/changelog/raw-json-translate-cache')),

  // ADR-WEB-12.46 PR2: 双线路 grouped endpoint (后端聚合, 不让前端硬 group)
  // status: success | aborted | both; 返 { success: { groups, total, ...}, aborted: { groups, ... } }
  tradeLifecycle: (params = {}) =>
    unwrap(client.get('/trade/lifecycle', { params })),

  // BL135 (2026-06-08): Agent 执行链路 — 后端已有 /api/v1/agent-decision-trace.
  // 入参三选一: lifeline_id | symbol+since_ts+until_ts | request_id. 返 {trace_id, context, disclaimer, events:[{phase, ts, ts_epoch, source, kind, tier, score_*, verdict, actionable_entry, confidence, ...}]}.
  // phase ∈ signal / candidate / gate / sizing / order / fill / exit / fail_safe / settlement / ...
  // maple-style observability: events 按 phase 分组, 每 phase 含完整后台过程证据.
  agentDecisionTrace: (params = {}) =>
    unwrap(client.get('/agent-decision-trace', { params })),

  // BL142a (2026-06-09): OTLP-like trace (Watson APPROVE). 同入参三选一.
  // 返 {trace_id, spans[], async_hops[], service_lanes[], window_ms, _meta} 用于 waterfall.
  // spans: {span_id, parent_span_id, name, phase, service_name, span_kind, start_ms, end_ms,
  //         duration_ms, render_kind(point|bar), duration_source(instant|async_latency),
  //         status, verdict, attributes, links, source_ref, source_ref_status(ok|derived_no_single_emit|unmapped)}
  // async_hops: {from_span, to_span, from_service, to_service, latency_ms, hop_type, confidence, inferred, method, rid?, verdict?}
  trace: (params = {}) =>
    unwrap(client.get('/trace', { params })),

  // BL142d-0 (2026-06-09): source_ref runtime resolve (Watson V2 chosen).
  // 入参 event_type. 返 {event_type, ontology_ref, candidates:[{file,line,context}],
  //   status: "exact" | "multiple_candidates" | "drift" | "unmapped" | "file_missing", verified_at}.
  // 实时 grep mac engine.py 真行号 (backend cache 5min), 不靠 BL142a 硬编码 _EVENT_SOURCE_REF.
  sourceRefResolve: (eventType) =>
    unwrap(client.get('/source-ref-resolve', { params: { event_type: eventType } })),

  // BL142d-0: 批量 audit 所有 allowlist event_type 漂移报告 (nightly / 手工).
  sourceRefAudit: () =>
    unwrap(client.get('/source-ref-audit')),

  // pnl
  pnlDaily: (date) => unwrap(client.get('/pnl/daily', { params: { date } })),
  pnlMonthly: (month) => unwrap(client.get('/pnl/monthly', { params: { month } })),

  // positions
  positionsStats: (range = '7d') =>
    unwrap(client.get('/positions/stats', { params: { range } })),

  // reorg U-E (2026-06-12): 历史出仓记录 (取代已关停的出仓飞书推送 cron 96e40b63)
  positionsExitHistory: (params = {}) =>
    unwrap(client.get('/positions/exit-history', { params })),

  // ADR-WEB-12.28: portfolio_intent_failed 聚合 (UI banner 用)
  intentFailures: (sinceHours = 1.0) =>
    unwrap(client.get('/intent/failures', { params: { since_hours: sinceHours } })),

  // dashboard summary (single endpoint feeding 4 cards + integrity_warning + tri-state pnl)
  // schema_version=dashboard_summary_v1
  dashboardSummary: () => unwrap(client.get('/dashboard/summary')),

  // dataflow board (v2.7 r3 schema.v1, 33 监控卡片 / 行 1=11 抓数据 + 行 2=13 维护 + 行 3=9 计算)
  dataflowCurrent: (row) =>
    unwrap(client.get('/dataflow/current', { params: row ? { row } : {} })),
  dataflowEvents: (params = {}) =>
    unwrap(client.get('/dataflow/events', { params })),
  // step 2.11: 健康度聚合,供前端 alerts banner 用
  dataflowHealth: () => unwrap(client.get('/dataflow/health')),
  // step 2.13 r6: LLM 智能大白话诊断 (deepseek-v4-flash + 12-facet view + pydantic-ai tools)
  // 真 LLM 1-2 次 tool call + retry: 15-25s 正常, 60s timeout 留余地
  dataflowDiagnose: (id, host) =>
    unwrap(client.post('/dataflow/diagnose', { id, ...(host ? { host } : {}) }, { timeout: 60000 })),
  // step 2.15: Blueprint 本体论可视化 (Vue Flow + dagre auto-layout)
  blueprintGraph: () => unwrap(client.get('/blueprint/graph')),
  blueprintEntity: (kind, name) => unwrap(client.get(`/blueprint/entity/${kind}/${name}`)),
  // step 3.x: 4 动态追踪 (D1 runtime / D2 edges / D3 relay+persistence / D4 health)
  dynamicsSnapshot: () => unwrap(client.get('/dynamics/snapshot')),
  dynamicsProcessHistory: (processId, hours = 6) =>
    unwrap(client.get(`/dynamics/process/${processId}/history?hours=${hours}`)),
  // step 4.x: Watson r2 三层模型 — entrypoint mapping + D5 风险告警
  blueprintGraphV2: () => unwrap(client.get('/blueprint/graph/v2')),
  blueprintProcessEntrypoints: (processId) =>
    unwrap(client.get(`/blueprint/process/${processId}/entrypoints`)),
  // v5 standardisation registries (D.1)
  blueprintAudit: () => unwrap(client.get('/blueprint/audit')),
  // J4b: agent flywheel lifecycle snapshot
  blueprintAgentLifecycle: () => unwrap(client.get('/blueprint/agent_lifecycle')),
  // W5 (Watson hTWEwY): runtime_proof_ledger — commit→reload→tick→first_seen_action 链
  blueprintRuntimeProofLedger: () => unwrap(client.get('/blueprint/runtime_proof_ledger')),
  // BL051 (Watson vUoZlu): 女王契约 → cc-connect 任务映射 (含 confidence/evidence)
  blueprintQcmMappings: () => unwrap(client.get('/blueprint/queen_contract_to_cc_task')),
  // BL058 (Watson 1qshNT 子5 MVP + 子6): alerts hub + service topology
  alertsHub: () => unwrap(client.get('/alerts/hub')),
  serviceTopology: () => unwrap(client.get('/blueprint/service_consumption_topology')),
  traderDualActorTopology: () => unwrap(client.get('/blueprint/trader_dual_actor_topology')),
  // BL060 deepseek 综合告警态势报告
  alertsDigestGenerate: (body) => unwrap(client.post('/alerts/digest/generate', body || { period: 'manual', period_hours: 4 }, { timeout: 90000 })),
  alertsDigestHistory: (limit = 50, period = null) => {
    const params = { limit }
    if (period) params.period = period
    return unwrap(client.get('/alerts/digest/history', { params }))
  },
  alertsDigestDetail: (digestId) => unwrap(client.get(`/alerts/digest/${digestId}`)),
  // BL061 latest digest + cron 健康自检
  alertsDigestLatest: () => unwrap(client.get('/alerts/digest/latest')),
  // BL068 alerts 24h stats
  alertsStats: () => unwrap(client.get('/alerts/stats')),
  // BL069 切 live 阻塞清单
  liveSwitchBlockers: () => unwrap(client.get('/blueprint/live_switch_blockers')),
  // BL071 全 SoT 搜索索引
  blueprintSearchIndex: () => unwrap(client.get('/blueprint/search_index')),
  // N4: 6-subject runtime state (mac collector → backend → frontend)
  blueprintRuntimeState: () => unwrap(client.get('/blueprint/runtime_state/snapshot')),
  // Q1: agent-native 4-layer tree (Actor → Capability → Runtime → Evidence)
  blueprintAgentSystem: () => unwrap(client.get('/blueprint/agent_system')),
  // P3: data lineage SoT — what each tab reads, which tabs share truth
  blueprintDataLineage: () => unwrap(client.get('/blueprint/data_lineage')),
  // C1: unified display metadata for every Blueprint entity (label_zh/short_zh/plain_zh/rationale_zh keyed by technical_id)
  blueprintDisplayDictionary: () => unwrap(client.get('/blueprint/display_dictionary')),
  blueprintUnmappedEntrypoints: () =>
    unwrap(client.get('/blueprint/entrypoints/unmapped')),
  // BL143 v1 (reorg U-H, 2026-06-12): cc-connect runtime 任务 live 快照
  blueprintCcJobs: () => unwrap(client.get('/blueprint/cc_jobs')),
  riskAlerts: (hours = 24, processId = null, severity = null) => {
    const params = new URLSearchParams({ hours: String(hours) })
    if (processId) params.set('process_id', processId)
    if (severity) params.set('severity', severity)
    return unwrap(client.get(`/dynamics/risk_alerts?${params}`))
  },
}

export default api
